import classnames from 'classnames';
import { BorderedSwitch, BorderedSwitchSkeleton } from 'components/shared/BorderedSwitch';
import { FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { useSPIntl } from 'hooks/useIntl';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import type { LoadingResult, MergeDeal } from 'models/forms/AddonFormResult';
import { FC, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';

import { AddonMainFormContext } from '../AddonMainForm.context';
import { useExistsValidationErrorFor } from '../ValidationErrorsContext';

export type OwnProps = {
  deals: LoadingResult<readonly MergeDeal[]> | undefined;
  isSwitchDisabled: boolean;
  mergeDealsRequested: boolean;
  className?: string;
  selectedTradeId?: string;

  onLineSelected: (tradeId: string | undefined) => void;
  onMergeDealsRequestedChange: (newMergedDealRequested: boolean) => void;
};

export interface TableProps {
  e2e?: string;
  className?: string;
}

const MergeDealsTableSkeleton: FC = () => (
  <div>
    <table className="table">
      <thead>
        <tr>
          <th>
            <Skeleton width="5em" />
          </th>
          <th>
            <Skeleton width="8em" />
          </th>
          <th>
            <Skeleton width="5em" />
          </th>
          <th>
            <Skeleton width="5em" />
          </th>
          <th>
            <Skeleton width="5em" />
          </th>
          <th>
            <Skeleton width="5em" />
          </th>
          <th className="text-end">
            <Skeleton width="10em" />
          </th>
          <th className="text-end">
            <Skeleton width="7em" />
          </th>
          <th className="text-end">
            <Skeleton width="7em" />
          </th>
          <th className="text-end">
            <Skeleton width="7em" />
          </th>
          <th className="text-end">
            <Skeleton width="7em" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <Skeleton width="5em" />
          </td>
          <td>
            <Skeleton width="5em" />
          </td>
          <td>
            <Skeleton width="5em" />
          </td>
          <td>
            <Skeleton width="5em" />
          </td>
          <td>
            <Skeleton width="5em" />
          </td>
          <td>
            <Skeleton width="5em" />
          </td>
          <td className="no-break text-end">
            <Skeleton width="5em" />
            &nbsp;
            <Skeleton width="3em" />
          </td>
          <td className="no-break text-end">
            <Skeleton width="4em" />
            &nbsp;
            <Skeleton width="1em" />
          </td>
          <td className="no-break text-end">
            <Skeleton width="4em" />
            &nbsp;
            <Skeleton width="1em" />
          </td>
          <td className="no-break text-end">
            <Skeleton width="4em" />
            &nbsp;
            <Skeleton width="1em" />
          </td>
          <td className="no-break text-end">
            <Skeleton width="4em" />
            &nbsp;
            <Skeleton width="1em" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const MergeDealsTable: FC<OwnProps> = ({ className, selectedTradeId, deals, onLineSelected }) => {
  const isInternalUser = useIsInternalUser();
  const formContext = useContext(AddonMainFormContext);
  const { formatDisplayedDate } = useSPIntl();
  const hasEliotCode = useMemo(
    () => deals?.data?.some(row => row.tradeInfo?.counterparty?.eliotCode !== undefined) ?? false,
    [deals?.data],
  );
  const hasBdrMnemonic = useMemo(
    () => deals?.data?.some(row => row.tradeInfo?.counterparty?.bdrMnemonic !== undefined) ?? false,
    [deals?.data],
  );
  if (!deals || !deals.isSuccess || !deals.data) {
    return null;
  }

  return (
    <>
      <div data-e2e="addon-merge-deals">
        <table className={classnames('table table-hover', className)} data-e2e="0">
          <thead>
            <tr>
              <th>
                <FormattedMessage id="addon.mergeDeals.column.header.reference" />
              </th>
              <th>
                <FormattedMessage id="addon.mergeDeals.column.header.status" />
              </th>
              <th>
                <FormattedMessage id="addon.mergeDeals.column.header.tradedate" />
              </th>
              <th>
                <FormattedMessage id="addon.mergeDeals.column.header.valuedate" />
              </th>
              {hasEliotCode && (
                <th>
                  <FormattedMessage id="addon.mergeDeals.column.header.eliotCode" />
                </th>
              )}
              {hasBdrMnemonic && !hasEliotCode && (
                <th>
                  <FormattedMessage id="addon.mergeDeals.column.header.bdrMnemonic" />
                </th>
              )}
              <th>
                <FormattedMessage id="addon.mergeDeals.column.header.tradeWay" />
              </th>
              <th className="text-end">
                <FormattedMessage id="addon.mergeDeals.column.header.notional" />
              </th>
              <th className="text-end">
                <FormattedMessage id="addon.mergeDeals.column.header.upfront" />
              </th>
              <th className="text-end">
                <FormattedMessage id="addon.mergeDeals.column.header.salesMargin" />
              </th>
              <th className="text-end">
                <FormattedMessage id="addon.mergeDeals.column.header.tradingPrice" />
              </th>
              <th className="text-end">
                <FormattedMessage id="addon.mergeDeals.column.header.offerPrice" />
              </th>
            </tr>
          </thead>
          <tbody>
            {deals.data!.map((line, key) => (
              <tr
                key={key}
                className={classnames({ 'table-info': line.tradeReference === selectedTradeId })}
                onClick={() => {
                  if (formContext.isReadOnly) {
                    return;
                  }
                  if (line.tradeReference === selectedTradeId) {
                    onLineSelected(undefined);
                  } else {
                    onLineSelected(line.tradeReference);
                  }
                }}
              >
                <td>{line.tradeReference}</td>
                <td>{line.tradeInfo?.workflowStatus}</td>
                <td>{line.tradeInfo?.tradeDate && formatDisplayedDate(line.tradeInfo?.tradeDate)}</td>
                <td>{line.tradeInfo?.valueDate && formatDisplayedDate(line.tradeInfo?.valueDate)}</td>
                {hasEliotCode && <td>{line.tradeInfo?.counterparty?.eliotCode}</td>}
                {hasBdrMnemonic && !hasEliotCode && <td>{line.tradeInfo?.counterparty?.bdrMnemonic}</td>}
                <td>
                  {line.tradeInfo?.tradeWay && (
                    <FormattedMessage
                      id={`addon.${isInternalUser ? 'internalUser' : 'externalUser'}.${line.tradeInfo?.tradeWay}`}
                    />
                  )}
                </td>
                <td className="no-break text-end">
                  <FormattedNumber value={line.productInfo?.notional} />
                  &nbsp;{line.tradeInfo?.currency}
                </td>
                <td className="no-break text-end">
                  {line.tradeInfo?.upFront && (
                    <FormattedNumber value={line.tradeInfo?.upFront} maximumFractionDigits={4} style="percent" />
                  )}
                </td>
                <td className="no-break text-end">
                  {line.tradeInfo?.salesMargin && (
                    <FormattedNumber value={line.tradeInfo?.salesMargin} maximumFractionDigits={4} style="percent" />
                  )}
                </td>
                <td className="no-break text-end">
                  {line.tradeInfo?.tradingPrice && (
                    <FormattedNumber value={line.tradeInfo.tradingPrice} maximumFractionDigits={4} style="percent" />
                  )}
                </td>
                <td className="no-break text-end">
                  {line.productInfo?.offerPrice && (
                    <FormattedNumber value={line.productInfo.offerPrice} maximumFractionDigits={4} style="percent" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const MergeDeals: FC<OwnProps> = ({
  deals,
  isSwitchDisabled,
  mergeDealsRequested,
  onMergeDealsRequestedChange,
  ...otherProps
}) => {
  const { formatMessage } = useIntl();
  const { isLoading: isFormLoading, isReadOnly } = useContext(AddonMainFormContext);

  const areDealsLoading = deals === undefined;
  const isLoading = isFormLoading || areDealsLoading;

  const hasMergeDealErrors = useExistsValidationErrorFor('mergeDeals');

  return (
    <div
      className={classnames({
        'border-top border-bottom border-warning': hasMergeDealErrors,
      })}
    >
      {!isFormLoading && (
        <BorderedSwitch
          id="addon-investment-details-mergedeals"
          isDisabled={isSwitchDisabled || isReadOnly || areDealsLoading}
          value={mergeDealsRequested}
          onValueChanged={onMergeDealsRequestedChange}
          label={formatMessage({ id: 'addon.mergeDealsRequested' })}
          className="mb-1"
        />
      )}

      {isFormLoading && <BorderedSwitchSkeleton />}

      {!isLoading && mergeDealsRequested && (
        <MergeDealsTable
          deals={deals}
          isSwitchDisabled={isSwitchDisabled}
          mergeDealsRequested={mergeDealsRequested}
          onMergeDealsRequestedChange={onMergeDealsRequestedChange}
          {...otherProps}
        />
      )}

      {isLoading && mergeDealsRequested && <MergeDealsTableSkeleton />}
    </div>
  );
};
